/**
 * Receipt Formatter Utility
 * Generates formatted text content for thermal printing based on receipt configuration
 *
 * Supports all configuration fields:
 * - showLogo: Logo image (URL or base64) - handled by Print Agent
 * - showItemCode: Item codes
 * - showItemTax: Per-item tax
 * - showItemDiscount: Per-item discount
 * - showQrCode + qrCodeData: QR code for UPI/payment - handled by Print Agent
 * - kotFontSize: Font size metadata for KOT
 *
 * Note: Images (logo, QR code) are passed as metadata to the Print Agent,
 * which handles ESC/POS commands for thermal printer image rendering.
 */

import brandingConfig from '../config/brandingConfig';

/**
 * Center text helper
 */
function center(text, width) {
  const padding = Math.max(0, Math.floor((width - text.length) / 2));
  return ' '.repeat(padding) + text;
}

/**
 * Two column helper
 */
function twoCol(left, right, width) {
  const rightStr = String(right);
  const space = Math.max(1, width - left.length - rightStr.length);
  return left + ' '.repeat(space) + rightStr;
}

/**
 * Three column helper for items with code, name, qty, amount
 */
function threeCol(left, middle, right, width) {
  const rightStr = String(right);
  const leftStr = String(left);
  const middleWidth = width - leftStr.length - rightStr.length - 2;
  const truncatedMiddle = middle.length > middleWidth ? middle.substring(0, middleWidth) : middle;
  const space = Math.max(1, middleWidth - truncatedMiddle.length);
  return leftStr + ' ' + truncatedMiddle + ' '.repeat(space) + rightStr;
}

/**
 * Four column helper for items: Description | Qty | Rate | Amount
 * Column widths differ by paper size:
 *   80mm (48 chars): Desc=24, Qty=4, Rate=8, Amt=12
 *   58mm (32 chars): Desc=13, Qty=3, Rate=7, Amt=9
 */
function fourCol(desc, qty, rate, amt, width) {
  let descW, qtyW, rateW, amtW;
  if (width <= 32) {
    // 58mm — tighter layout, give more space to rate/amount
    descW = 13; qtyW = 3; rateW = 7; amtW = 9;
  } else {
    // 80mm — standard 12-unit grid
    descW = 24; qtyW = 4; rateW = 8; amtW = 12;
  }

  const descStr = desc.length > descW ? desc.substring(0, descW) : desc;
  const qtyStr = String(qty).padStart(qtyW);
  const rateStr = String(rate).padStart(rateW);
  const amtStr = String(amt).padStart(amtW);

  return descStr + ' '.repeat(Math.max(0, descW - descStr.length)) + qtyStr + rateStr + amtStr;
}

/**
 * Three column helper for items without rate: Description | Qty | Amount
 * Column widths differ by paper size:
 *   80mm (48 chars): Desc=28, Qty=8, Amt=12
 *   58mm (32 chars): Desc=18, Qty=5, Amt=9
 */
function threeColItem(desc, qty, amt, width) {
  let descW, qtyW, amtW;
  if (width <= 32) {
    descW = 18; qtyW = 5; amtW = 9;
  } else {
    descW = 28; qtyW = 8; amtW = 12;
  }

  const descStr = desc.length > descW ? desc.substring(0, descW) : desc;
  const qtyStr = String(qty).padStart(qtyW);
  const amtStr = String(amt).padStart(amtW);

  return descStr + ' '.repeat(Math.max(0, descW - descStr.length)) + qtyStr + amtStr;
}

/**
 * Generate QR code data string for UPI payment
 * @param {Object} data - Payment data
 * @returns {string} UPI deep link string
 */
export function generateUPIQRData(data) {
  if (!data) return '';
  const upiId = (data.upiId || data.merchantUpiId || '').trim();
  const merchantName = (data.merchantName || data.restaurantName || 'Merchant').trim();
  const amount = data.amount || data.finalAmount || 0;
  const txnNote = data.billNumber ? `Bill${data.billNumber}` : 'Payment';

  if (!upiId) return '';

  // UPI deep link format
  // Note: UPI ID (pa) should NOT be URL encoded as @ is valid in VPA
  // Merchant name should have spaces replaced with %20 (not +)
  // Amount should be a plain number without trailing zeros
  const amountStr = amount > 0 ? amount.toFixed(2).replace(/\.?0+$/, '') : '0';

  // Clean merchant name - remove special characters that might break QR
  const cleanName = merchantName.replace(/[&=?#]/g, '').substring(0, 50);

  return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(cleanName)}&am=${amountStr}&tn=${txnNote}&cu=INR`;
}

/**
 * Generate formatted Bill receipt content
 * @param {Object} config - Receipt configuration from database
 * @param {Object} data - Bill data (items, totals, payment info, etc.)
 * @returns {Object} Object with content string and metadata for images
 *   - content: Formatted receipt text
 *   - logo: { show: boolean, url: string, base64: string } - Logo data for Print Agent
 *   - qrCode: { show: boolean, data: string } - QR code data for Print Agent
 */
export function generateBillContent(config, data) {
  const width = config.paperWidth === '58mm' ? 32 : 48;

  const line = '='.repeat(width);
  const dashed = '-'.repeat(width);
  const doubleDashed = '- '.repeat(Math.floor(width / 2));

  let content = [];

  // Metadata for images (handled by Print Agent with ESC/POS commands)
  // Logo URL comes from branch details (data.logoUrl)
  const metadata = {
    logo: {
      show: config.showLogo || false,
      url: data.logoUrl || null, // From branchDetails.logoUrl
    },
    qrCode: {
      show: config.showQrCode || false,
      // If qrCodeData is a full upi:// link, use as-is; otherwise treat it as a UPI ID
      // and generate a proper UPI deep link with amount embedded
      data: (() => {
        const configQR = (config.qrCodeData || '').trim();
        if (configQR.startsWith('upi://')) return configQR;
        if (configQR) return generateUPIQRData({ ...data, upiId: configQR });
        return generateUPIQRData(data) || null;
      })(),
      size: config.qrCodeSize || 6,              // Module size for thermal print (1-8, default 6)
      errorCorrection: config.qrErrorCorrection || 'M', // L=7%, M=15%, Q=25%, H=30%
    },
    paperWidth: config.paperWidth || '80mm',
  };

  // ========== REPRINT BADGE ==========

  if (data.isReprint) {
    content.push('[CENTER][BOLD]** REPRINT **');
    content.push('');
  }

  // ========== HEADER SECTION ==========

  // Logo placeholder marker (actual image rendered by Print Agent)
  if (config.showLogo && metadata.logo.url) {
    content.push('{{LOGO}}');
    content.push('');
  }

  // Restaurant Name — large bold font (matching mobile's double-width)
  if (config.showRestaurantName) {
    content.push('[CENTER][BOLD]' + (data.restaurantName || brandingConfig.receiptFallbackName));
  }

  // Address
  if (config.showAddress && data.address) {
    const maxLineWidth = width - 4;
    if (data.address.length > maxLineWidth) {
      const words = data.address.split(' ');
      let currentLine = '';
      words.forEach(word => {
        if ((currentLine + ' ' + word).trim().length <= maxLineWidth) {
          currentLine = (currentLine + ' ' + word).trim();
        } else {
          if (currentLine) content.push('[CENTER]' + currentLine);
          currentLine = word;
        }
      });
      if (currentLine) content.push('[CENTER]' + currentLine);
    } else {
      content.push('[CENTER]' + data.address);
    }
  }

  // Phone
  if (config.showPhone && data.phone) {
    content.push('[CENTER]' + data.phone);
  }

  // FSSAI — two lines matching mobile format
  if (config.showFssai && data.fssai) {
    content.push('[CENTER]FSSAI NO');
    content.push('[CENTER]' + data.fssai);
  }

  // GSTIN
  if (config.showGstin && data.gstin) {
    content.push('[CENTER]GSTN :' + data.gstin);
  }

  // Custom Header Text
  if (config.headerText) {
    content.push('[CENTER]' + config.headerText);
  }

  content.push('');

  // ========== BILL INFO SECTION ==========

  content.push('[CENTER][BOLD]TAX INVOICE');

  // Split Bill Banner
  if (data.splitLabel) {
    content.push('[CENTER][BOLD]** ' + data.splitLabel + ' **');
  }

  // NC Bill Banner (matching mobile's bold centered banner)
  if (data.isNc || data.ncAmount > 0) {
    content.push('[CENTER][BOLD]*** NON-CHARGEABLE BILL ***');
  }

  content.push(line);

  // Table No + Cover on same row (matching mobile layout)
  const halfWidth = Math.floor(width / 2);
  const rawTableName = data.tableName || 'N/A';
  // Callers either pass the raw MAVERICK_COUNTER name or the already-mapped "Takeaway"
  const upperTableName = rawTableName.toUpperCase();
  const isCounter = upperTableName.includes('MAVERICK_COUNTER') || upperTableName === 'TAKEAWAY';
  const displayTableName = isCounter ? 'Takeaway' : rawTableName;
  const tableName = isCounter ? displayTableName : `${displayTableName}${data.tableSplit ? ` (${data.tableSplit})` : ''}`;
  const tableStr = `Table No : ${tableName}`;
  const coverStr = data.pax ? `Cover : ${data.pax}` : '';
  if (coverStr) {
    content.push(tableStr + ' '.repeat(Math.max(1, halfWidth - tableStr.length)) + coverStr);
  } else {
    content.push(tableStr);
  }

  // Bill Number
  if (data.billNumber || data.receiptNumber) {
    content.push(`Bill No   : ${data.billNumber || data.receiptNumber}`);
  }

  // Bill Date (date only — time is shown in items row)
  // data.timestamp may be an ISO string, a Date, or a backend "DD-Mon-YYYY, HH:MI:SS AM"
  // string. Anything unparseable falls back to now instead of printing "Invalid Date".
  const parsedTimestamp = data.timestamp ? new Date(data.timestamp) : null;
  const billMoment = parsedTimestamp && !isNaN(parsedTimestamp.getTime())
    ? parsedTimestamp
    : new Date();
  const billDate = billMoment.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  content.push(`Bill Date : ${billDate}`);

  // Bill Type
  content.push(`Bill Type : ${data.orderType || (isCounter ? 'Takeaway' : 'Dine In')}`);

  // Steward / Waiter (always show — default to "Server" like mobile)
  content.push(`Steward   : ${data.waiterName || 'Server'}`);

  // Customer Info (controlled by receipt config toggle)
  const hasCustomer = data.customerName || data.customerPhone;
  if (hasCustomer && config.showCustomerInfo !== false) {
    content.push(line);
    content.push('CUSTOMER');
    if (data.customerName) content.push(`Name      : ${data.customerName}`);
    if (data.customerPhone) content.push(`Phone     : ${data.customerPhone}`);
    if (data.customerEmail) content.push(`Email     : ${data.customerEmail}`);
    if (data.customerAddress) content.push(`Address   : ${data.customerAddress}`);
  }

  content.push(line);

  // ========== ITEMS SECTION ==========

  // No of Items + Time on same row (matching mobile layout)
  const totalItems = (data.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0);
  // Derived from the bill's own timestamp so a reprint shows the original time, not print time
  const timeStr = data.time || billMoment.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  const itemCountStr = `No of Items : ${totalItems}`;
  const timeLabel = `Time : ${timeStr}`;
  content.push(itemCountStr + ' '.repeat(Math.max(1, halfWidth - itemCountStr.length)) + timeLabel);

  content.push(line);

  // Column headers — matching mobile 4-column layout
  const descMaxW = config.showUnitPrice
    ? (width <= 32 ? 13 : 24)
    : (width <= 32 ? 18 : 28);

  if (config.showUnitPrice) {
    content.push('[BOLD]' + fourCol('Description', 'Qty', 'Rate', 'Amount', width));
  } else {
    content.push('[BOLD]' + threeColItem('Description', 'Qty', 'Amount', width));
  }
  content.push(line);

  // ========== ITEMS ==========

  (data.items || []).forEach(item => {
    const itemName = item.name || item.itemName || 'Unknown Item';
    const itemCode = item.itemCode || item.code || '';
    const qty = item.quantity || 1;
    const price = item.price || item.unitPrice || item.finalPrice || 0;
    const total = item.total || item.lineTotal || (price * qty);
    const itemTax = item.taxAmount || item.tax || 0;
    const itemDiscount = item.discountAmount || item.discount || 0;
    const isNcItem = item.isComp || item.isNc || false;

    if (config.showItemDescription) {
      // Build display name with NC prefix and optional code prefix
      let displayName = itemName;
      if (isNcItem) {
        displayName = '[NC] ' + displayName;
      }
      if (config.showItemCode && itemCode) {
        displayName = `[${itemCode}] ${displayName}`;
      }

      // Item rows are bold (matching mobile)
      if (displayName.length <= descMaxW) {
        if (config.showUnitPrice) {
          content.push('[BOLD]' + fourCol(displayName, String(qty), price.toFixed(2), total.toFixed(2), width));
        } else {
          content.push('[BOLD]' + threeColItem(displayName, String(qty), total.toFixed(2), width));
        }
      } else {
        content.push('[BOLD]' + displayName);
        if (config.showUnitPrice) {
          content.push('[BOLD]' + fourCol('', String(qty), price.toFixed(2), total.toFixed(2), width));
        } else {
          content.push('[BOLD]' + threeColItem('', String(qty), total.toFixed(2), width));
        }
      }

      // Variant name if available
      if (item.variantName) {
        content.push(`  [${item.variantName}]`);
      }

      // Per-item tax (if enabled and has tax)
      if (config.showItemTax && itemTax > 0) {
        const taxRate = item.taxRate || item.taxPercentage || 0;
        if (taxRate > 0) {
          content.push(`  Tax (${taxRate.toFixed(1)}%): ${itemTax.toFixed(2)}`);
        } else {
          content.push(`  Tax: ${itemTax.toFixed(2)}`);
        }
      }

      // Per-item discount (if enabled and has discount)
      if (config.showItemDiscount && itemDiscount > 0) {
        const discRate = item.discountRate || item.discountPercentage || 0;
        if (discRate > 0) {
          content.push(`  Discount (${discRate.toFixed(1)}%): -${itemDiscount.toFixed(2)}`);
        } else {
          content.push(`  Discount: -${itemDiscount.toFixed(2)}`);
        }
      }
    }
  });

  content.push(line);

  // ========== TOTALS SECTION ==========

  // Discount (subtotal is implicit from items — not printed separately like mobile)
  if (config.showDiscountTotal && data.discountAmount > 0) {
    const discountLabel = data.discountPercentage > 0
      ? `Discount (${data.discountPercentage.toFixed(1)}%):`
      : 'Discount:';
    content.push(twoCol(discountLabel, `-${data.discountAmount.toFixed(2)}`, width));

    if (data.discountCode) {
      content.push(`  Code: ${data.discountCode}`);
    }
    if (data.discountReason) {
      content.push(`  (${data.discountReason})`);
    }
  }

  // NC Deduction (matching mobile)
  if (data.ncAmount > 0) {
    content.push(twoCol('NC Deduction:', `-${data.ncAmount.toFixed(2)}`, width));
  }

  // Tax Breakdown — always show CGST/SGST split (matching mobile)
  if (config.showTaxBreakdown && (data.taxAmount > 0 || data.cgstAmount > 0 || data.sgstAmount > 0)) {
    if (data.cgstAmount != null && data.sgstAmount != null) {
      // Use actual amounts passed from settlement
      const cgstRate = data.cgstRate || (data.taxPercentage ? data.taxPercentage / 2 : 0);
      const sgstRate = data.sgstRate || cgstRate;
      content.push(twoCol(`CGST@${cgstRate.toFixed(1)} :`, (data.cgstAmount || 0).toFixed(2), width));
      content.push(twoCol(`SGST@${sgstRate.toFixed(1)} :`, (data.sgstAmount || 0).toFixed(2), width));
    } else if (data.taxAmount > 0) {
      // Fallback: split tax 50/50 (matching mobile behavior)
      const halfTax = data.taxAmount / 2;
      const halfPercent = (data.taxPercentage || 0) / 2;
      content.push(twoCol(`CGST@${halfPercent.toFixed(1)} :`, halfTax.toFixed(2), width));
      content.push(twoCol(`SGST@${halfPercent.toFixed(1)} :`, halfTax.toFixed(2), width));
    }
  }

  // Round Off
  if (data.roundOff !== undefined && data.roundOff !== 0) {
    const sign = data.roundOff >= 0 ? '+' : '';
    content.push(twoCol('Round Off:', `${sign}${data.roundOff.toFixed(2)}`, width));
  }

  // Payable Amount — large bold font (matching mobile's double-width)
  if (config.showGrandTotal) {
    content.push(line);
    if (data.isNc && (data.finalAmount === 0 || !data.finalAmount)) {
      content.push('[BOLD]' + twoCol('No Charge', '0.00', width));
    } else {
      content.push('[BOLD]' + twoCol('Payable Amt', (data.finalAmount || 0).toFixed(2), width));
    }
    content.push(line);
  }

  // ========== PAYMENT INFO ==========

  if (config.showPaymentMethod && data.paymentMode) {
    const paymentLabel = data.paymentMode === 'CASH' ? 'Cash'
      : data.paymentMode === 'ONLINE' ? 'UPI/Online'
      : data.paymentMode === 'CARD' ? 'Card'
      : data.paymentMode === 'SPLIT' ? 'Split Payment'
      : data.paymentMode;

    content.push(`Payment : ${paymentLabel}`);

    if (data.paymentMode === 'CASH' && data.cashReceived > 0) {
      content.push(twoCol('Received:', data.cashReceived.toFixed(2), width));
      content.push(twoCol('Change:', (data.changeDue || 0).toFixed(2), width));
    }

    if ((data.paymentMode === 'ONLINE' || data.paymentMode === 'UPI') && data.transactionId) {
      content.push(twoCol('Txn ID:', data.transactionId, width));
    }

    if (data.paymentMode === 'SPLIT') {
      if (data.cashPart > 0) content.push(twoCol('  Cash:', data.cashPart.toFixed(2), width));
      if (data.onlinePart > 0) content.push(twoCol('  UPI:', data.onlinePart.toFixed(2), width));
      if (data.cardPart > 0) content.push(twoCol('  Card:', data.cardPart.toFixed(2), width));
    }

    content.push(line);
  }

  // ========== QR CODE SECTION ==========

  if (config.showQrCode) {
    content.push('');
    content.push(line);
    content.push('[CENTER][BOLD]SCAN TO PAY');
    content.push('');

    if (metadata.qrCode.data) {
      // QR Code marker (actual QR image rendered by Print Agent with ESC/POS commands)
      content.push('{{QRCODE}}');
      content.push('');

      // Show UPI ID below QR code for reference
      // Priority: qrCodeData (receipt config) > upiId (payment account) > merchantUpiId (branch)
      const displayUpiId = config.qrCodeData || data.upiId || data.merchantUpiId || '';
      if (displayUpiId) {
        content.push('[CENTER]' + displayUpiId);
      }
    } else {
      // No UPI data configured - show placeholder message
      content.push('[CENTER][QR Code Not Available]');
      content.push('[CENTER]Configure UPI ID in Receipt Settings');
    }

    content.push(line);
  }

  // ========== FOOTER SECTION ==========

  // Thank You Message — bold centered uppercase (matching mobile)
  if (config.showThankYou && config.thankYouText) {
    content.push('');
    content.push('[CENTER][BOLD]' + config.thankYouText.toUpperCase());
  }

  // Custom Footer Text
  if (config.footerText) {
    content.push('');
    content.push('[CENTER]' + config.footerText);
  }

  // E & OE
  content.push('[CENTER]E & OE');

  content.push('');
  content.push(''); // Extra lines for paper cut

  return {
    content: content.join('\n'),
    ...metadata,
  };
}

/**
 * Generate formatted KOT receipt content
 * @param {Object} config - Receipt configuration from database
 * @param {Object} data - KOT data (items, table, station, etc.)
 * @returns {Object} Object with content string and metadata (fontSize)
 */
export function generateKOTContent(config, data) {
  const width = config.paperWidth === '58mm' ? 32 : 48;

  const line = '='.repeat(width);
  const dashed = '-'.repeat(width);

  const fontSize = config.kotFontSize || 'normal';

  let content = [];

  // ========== HEADER (matching mobile) ==========

  // KOT title — bold centered (mobile uses double-width)
  content.push('[CENTER][BOLD]** KOT **');

  // Kitchen Station — right after title
  if (config.showKitchenStation && data.stationName) {
    content.push('[CENTER][BOLD][ ' + data.stationName.toUpperCase() + ' ]');
  }

  // KOT Number
  if (config.showOrderNumber) {
    content.push('[CENTER][BOLD]#' + (data.kotNumber || 'N/A'));
  }

  content.push(line);

  // ========== ORDER INFO (matching mobile) ==========

  if (config.showTableNumber) {
    const rawName = data.tableName || 'N/A';
    const isCounter = rawName.toUpperCase().includes('MAVERICK_COUNTER');
    const displayName = isCounter ? 'Takeaway' : rawName;
    const tableInfo = isCounter ? displayName : `${displayName}${data.tableSplit ? ` (${data.tableSplit})` : ''}`;
    content.push('[BOLD]' + twoCol('Table:', tableInfo, width));
  }

  if (config.showWaiterName) {
    content.push(twoCol('Waiter:', data.waiterName || 'Server', width));
  }

  if (config.showTimestamp) {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const mi = String(now.getMinutes()).padStart(2, '0');
    const timeStr = `${dd}/${mm} ${hh}:${mi}`;
    content.push(twoCol('Time:', timeStr, width));
  }

  content.push(dashed);

  // ========== ITEMS HEADER (matching mobile) ==========

  content.push('[BOLD]' + twoCol('Item', 'Qty', width));
  content.push(dashed);

  // ========== ITEMS (matching mobile — no separators between items) ==========

  (data.items || []).forEach((item) => {
    const itemName = item.name || item.itemName || 'Unknown';
    const itemCode = item.itemCode || item.code || '';
    const variantName = item.variantName || '';
    const qty = item.quantity || 1;

    // Build display name: [code] name (variant) — all on one line like mobile
    let displayName = '';
    if (config.showItemCode && itemCode) {
      displayName = `#${itemCode} ${itemName}`;
    } else {
      displayName = itemName;
    }
    if (variantName) {
      displayName += ` (${variantName})`;
    }

    // Item row — bold, fontA (matching mobile)
    content.push('[BOLD]' + twoCol(displayName, `x${qty}`, width));

    // Per-item special instructions — indented (matching mobile's fontB >> format)
    if (item.customization) {
      if (item.customization.customInstructions) {
        content.push(`  >> ${item.customization.customInstructions}`);
      }
      if (item.customization.size) {
        content.push(`  >> SIZE: ${item.customization.size}`);
      }
      if (item.customization.spiceLevel) {
        content.push(`  >> SPICE: ${item.customization.spiceLevel}`);
      }
      if (item.customization.preparation) {
        content.push(`  >> PREP: ${item.customization.preparation}`);
      }
      if (item.customization.quickNotes?.length > 0) {
        content.push(`  >> ${item.customization.quickNotes.join(', ')}`);
      }
    }

    if (item.notes) {
      content.push(`  >> ${item.notes}`);
    }
  });

  content.push(dashed);

  // ========== NOTES (matching mobile — inline bold, not boxed) ==========

  if (config.showSpecialInstructions) {
    if (data.specialInstructions) {
      content.push('[BOLD]NOTE: ' + data.specialInstructions);
      content.push(dashed);
    }
    if (data.chefNotes) {
      content.push('[BOLD]CHEF NOTE: ' + data.chefNotes);
      content.push(dashed);
    }
  }

  // ========== FOOTER (matching mobile — simple centered) ==========

  const totalQty = (data.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0);
  content.push('[CENTER][BOLD]Total Items: ' + totalQty);

  content.push('');
  content.push('');

  return {
    content: content.join('\n'),
    fontSize: fontSize,
    kotNumber: data.kotNumber,
    station: data.stationName
  };
}

/**
 * Generate formatted Cancel KOT receipt content
 * @param {Object} config - Receipt configuration from database
 * @param {Object} data - Cancel KOT data (items, table, station, reason, etc.)
 * @returns {Object} Object with content string and metadata
 */
export function generateCancelKOTContent(config, data) {
  const width = config.paperWidth === '58mm' ? 32 : 48;

  const line = '='.repeat(width);
  const dashed = '-'.repeat(width);

  const fontSize = config.kotFontSize || 'normal';

  let content = [];

  // ========== HEADER (matching KOT style) ==========

  content.push('[CENTER][BOLD]** CANCEL KOT **');
  content.push('[CENTER][BOLD]*** CANCELLED ***');

  if (config.showKitchenStation && data.stationName) {
    content.push('[CENTER][BOLD][ ' + data.stationName.toUpperCase() + ' ]');
  }

  if (config.showOrderNumber) {
    content.push('[CENTER][BOLD]#' + (data.kotNumber || 'N/A'));
  }

  content.push(line);

  // ========== ORDER INFO ==========

  if (config.showTableNumber) {
    const rawName = data.tableName || 'N/A';
    const isCounter = rawName.toUpperCase().includes('MAVERICK_COUNTER');
    const displayName = isCounter ? 'Takeaway' : rawName;
    const tableInfo = isCounter ? displayName : `${displayName}${data.tableSplit ? ` (${data.tableSplit})` : ''}`;
    content.push('[BOLD]' + twoCol('Table:', tableInfo, width));
  }

  if (config.showTimestamp) {
    const now = new Date();
    const timeStr = `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    content.push(twoCol('Cancelled:', timeStr, width));
  }

  if (data.cancelledBy) {
    content.push(twoCol('By:', data.cancelledBy, width));
  }

  if (data.cancelReason) {
    content.push('[BOLD]Reason: ' + data.cancelReason);
  }

  content.push(dashed);

  // ========== ITEMS ==========

  content.push('[BOLD]' + twoCol('Item', 'Qty', width));
  content.push(dashed);

  (data.items || []).forEach(item => {
    const itemName = item.name || item.itemName || 'Unknown';
    const variantName = item.variantName || '';
    const qty = item.quantity || 1;

    let displayName = itemName;
    if (variantName) displayName += ` (${variantName})`;

    content.push('[BOLD]' + twoCol(displayName, `x${qty}`, width));
  });

  content.push(dashed);

  // ========== WARNING ==========

  content.push('[CENTER][BOLD]DO NOT PREPARE');

  // Total
  const totalQty = (data.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0);
  content.push('[CENTER][BOLD]Total Cancelled: ' + totalQty);

  content.push('');
  content.push('');

  return {
    content: content.join('\n'),
    fontSize: fontSize,
    kotNumber: data.kotNumber,
    station: data.stationName,
    isCancellation: true
  };
}

/**
 * Default receipt configuration (fallback if no config from database)
 */
export const DEFAULT_RECEIPT_CONFIG = {
  // Basic
  paperWidth: '80mm',
  receiptType: 'BILL',
  isDefault: true,
  isActive: true,

  // Header
  showLogo: false, // Logo URL comes from branch details (branchDetails.logoUrl)
  showRestaurantName: true,
  showAddress: true,
  showPhone: true,
  showGstin: true,
  showFssai: false,
  headerText: '',

  // Items
  showItemCode: false,
  showItemDescription: true,
  showQuantity: true,
  showUnitPrice: true,
  showItemTax: false,
  showItemDiscount: false,

  // Totals
  showSubtotal: true,
  showTaxBreakdown: true,
  showDiscountTotal: true,
  showGrandTotal: true,
  showPaymentMethod: true,

  // Footer
  showThankYou: true,
  thankYouText: 'Thank you for dining with us!',
  footerText: '',
  showQrCode: false,
  qrCodeData: '',
  qrCodeSize: 6,              // Module size for thermal print: 1-8 (6 = ~2.5cm on 80mm paper)
  qrErrorCorrection: 'M',     // Error correction: L=7%, M=15%, Q=25%, H=30%

  // KOT Specific
  showOrderNumber: true,
  showTableNumber: true,
  showWaiterName: true,
  showTimestamp: true,
  showKitchenStation: true,
  showSpecialInstructions: true,
  kotFontSize: 'normal', // 'small', 'normal', 'large'
};
